import React, { useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { designType } from "services/constants/designTokens";
import { SelectField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { StatutoryPage, StatutoryTabs } from "pages/PayRoll/Shared/statutoryUi";
import {
  CODES,
  PAYROLL_MONTHS,
  SEPT_PAYABLE,
  accountsFor,
  buildIssues,
  buildRegister,
  exitRow,
  sampleChallans,
  sampleEcrFiles,
  sampleExits,
  sampleMapping,
} from "./pfReportsData";
import { PfOverviewTab } from "./PfOverviewTab";
import { PfRegisterTab } from "./PfRegisterTab";
import { PfEcrTab } from "./PfEcrTab";
import { PfExitsTab } from "./PfExitsTab";
import { PfReconciliationTab } from "./PfReconciliationTab";
import { PfValidationTab } from "./PfValidationTab";

// Payroll > Reports > Statutory Reports > PF (screens P7–P12a). Built from
// the finalised payroll: Cauver prepares the ECR file, the company uploads
// it and pays on the EPFO portal. Sample data until the PF APIs exist;
// generated files, challans and marked exits stay in memory for this visit.

const TABS = [
  { value: "overview", label: "Overview" },
  { value: "register", label: "Contribution Register" },
  { value: "ecr", label: "ECR File" },
  { value: "exits", label: "Exits" },
  { value: "reconciliation", label: "Reconciliation" },
  { value: "validation", label: "Validation" },
];

const FilterSelect = ({ label, value, options, onChange }) => (
  <Mui.Box sx={{ width: { xs: "100%", sm: 220 } }}>
    <Mui.Typography sx={{ ...designType.captionText, mb: 0.5 }}>{label}</Mui.Typography>
    <SelectField value={value} onChange={(next) => next !== "" && onChange(next)} options={options} />
  </Mui.Box>
);

export const PfStatutoryReports = ({ onBack }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  // Fresh sample data per company.
  return <PfReportsScreen key={`${domain}:${topbarCompanyId}`} onBack={onBack} />;
};

const PfReportsScreen = ({ onBack }) => {
  const [tab, setTab] = useState("overview");
  const [month, setMonth] = useState("2026-10");
  const [codeFilter, setCodeFilter] = useState("all");
  const [department, setDepartment] = useState("all");
  const [register] = useState(buildRegister);
  const [exits, setExits] = useState(sampleExits);
  const [mapping] = useState(sampleMapping);
  const [challans, setChallans] = useState(sampleChallans);
  const [ecrFiles, setEcrFiles] = useState(sampleEcrFiles);
  const [lastCheck, setLastCheck] = useState({ at: "2026-11-05T10:20:00", trigger: "UAN import (auto)" });

  const monthInfo = PAYROLL_MONTHS.find((item) => item.value === month);
  const departments = useMemo(() => [...new Set(register.map((row) => row.department))].sort(), [register]);

  const inScope = (row) => (codeFilter === "all" || String(row.code_id) === codeFilter) && (department === "all" || row.department === department);
  const rows = useMemo(() => register.filter(inScope), [register, codeFilter, department]); // eslint-disable-line react-hooks/exhaustive-deps
  const exitRows = useMemo(() => exits.map((exit) => exitRow(exit, mapping)).filter(inScope), [exits, mapping, codeFilter, department]); // eslint-disable-line react-hooks/exhaustive-deps
  const allIssues = useMemo(() => buildIssues(register, exits, mapping), [register, exits, mapping, lastCheck]); // eslint-disable-line react-hooks/exhaustive-deps
  const issues = useMemo(
    () =>
      allIssues.filter((issue) => {
        const person = register.find((row) => row.id === issue.emp_id) || exits.find((exit) => exit.id === issue.emp_id);
        return inScope({ code_id: issue.code_id, department: person?.department });
      }),
    [allIssues, register, exits, codeFilter, department], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Payable per code per month: the open month from the register, earlier
  // months fixed in the sample.
  const payableFor = (codeId, forMonth) => {
    if (forMonth === "2026-09") {
      const accounts = SEPT_PAYABLE[codeId];
      return { ...accounts, total: accounts.a1 + accounts.a10 + accounts.a2 + accounts.a21 };
    }
    return accountsFor(register.filter((row) => row.code_id === codeId));
  };
  const challansWithAmounts = challans.map((challan) => (challan.amounts ? challan : { ...challan, amounts: payableFor(challan.code_id, challan.month) }));
  const blockingFor = (codeId) => allIssues.filter((issue) => issue.severity === "Blocking" && issue.code_id === codeId);

  const shared = {
    month,
    monthInfo,
    codeFilter,
    rows,
    register,
    exitRows,
    issues,
    allIssues,
    payableFor,
    blockingFor,
    challans: challansWithAmounts,
    ecrFiles,
    goTo: setTab,
  };

  const aside = monthInfo.finalized ? (
    <Mui.Box sx={{ textAlign: { md: "right" } }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center" justifyContent={{ md: "flex-end" }}>
        <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
        <RequestStatusChip status="Finalized" colorCode="#12B76A" />
      </Mui.Stack>
      <Mui.Typography sx={{ ...designType.captionText, mt: 0.5 }}>
        Finalized {moment(monthInfo.finalizedAt).format("DD MMM YYYY, HH:mm")} · Version {monthInfo.version}
      </Mui.Typography>
    </Mui.Box>
  ) : (
    <Mui.Stack direction="row" spacing={1} alignItems="center">
      <Mui.Typography sx={designType.helperText}>Payroll status</Mui.Typography>
      <RequestStatusChip status="Not finalized" colorCode="#6B7280" />
    </Mui.Stack>
  );

  return (
    <StatutoryPage
      breadcrumb={["Payroll", "Reports", "Statutory Reports", "PF"]}
      title="PF Reports"
      subtitle="Built from finalised payroll. Cauver prepares the ECR file — your organisation uploads it and pays on the EPFO portal."
      aside={aside}
      onBack={onBack}
    >
      <Mui.Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }}>
        <FilterSelect label="Payroll Month" value={month} onChange={setMonth} options={PAYROLL_MONTHS.map((item) => ({ value: item.value, label: item.label }))} />
        <FilterSelect
          label="PF Code"
          value={codeFilter}
          onChange={setCodeFilter}
          options={[{ value: "all", label: "All codes" }, ...CODES.map((code) => ({ value: String(code.id), label: `${code.type === "MAIN" ? "Main" : "Extension"} · ${code.code}` }))]}
        />
        <FilterSelect
          label="Department"
          value={department}
          onChange={setDepartment}
          options={[{ value: "all", label: "All departments" }, ...departments.map((item) => ({ value: item, label: item }))]}
        />
      </Mui.Stack>

      <StatutoryTabs value={tab} onChange={setTab} tabs={TABS} />

      {!monthInfo.finalized && tab !== "reconciliation" ? (
        <RequestInfoBanner
          tone="warning"
          title={`${monthInfo.label} payroll is not finalised yet.`}
          body="PF reports open only for finalised payroll. Finalise the payroll in Pay Processing, then come back here. Reconciliation of earlier months is still available."
        />
      ) : (
        <>
          {tab === "overview" ? <PfOverviewTab {...shared} /> : null}
          {tab === "register" ? <PfRegisterTab {...shared} /> : null}
          {tab === "ecr" ? (
            <PfEcrTab
              {...shared}
              onGenerate={(codeId, lines) =>
                setEcrFiles((list) => {
                  const versions = list.filter((file) => file.code_id === codeId && file.month === month);
                  const version = Math.max(0, ...versions.map((file) => file.version)) + 1;
                  return [
                    { id: Math.max(0, ...list.map((file) => file.id)) + 1, code_id: codeId, month, version, lines, generated_at: moment().format("YYYY-MM-DDTHH:mm:ss"), status: "Current" },
                    ...list.map((file) => (file.code_id === codeId && file.month === month ? { ...file, status: "Superseded" } : file)),
                  ];
                })
              }
            />
          ) : null}
          {tab === "exits" ? (
            <PfExitsTab
              {...shared}
              onMarkDone={(id, change) => setExits((list) => list.map((exit) => (exit.id === id ? { ...exit, ...change } : exit)))}
            />
          ) : null}
          {tab === "reconciliation" ? (
            <PfReconciliationTab {...shared} onRecord={(challan) => setChallans((list) => [...list, { ...challan, id: Math.max(0, ...list.map((item) => item.id)) + 1 }])} />
          ) : null}
          {tab === "validation" ? (
            <PfValidationTab {...shared} lastCheck={lastCheck} onRecheck={() => setLastCheck({ at: moment().format("YYYY-MM-DDTHH:mm:ss"), trigger: "Re-check now" })} />
          ) : null}
        </>
      )}
    </StatutoryPage>
  );
};
